/**
 * Lightweight usage analytics for the web app.
 *
 * Events are queued and flushed in batches to the public `trackWebEvents`
 * callable, which increments Admin-SDK-only counters in `web_analytics/*`
 * (works for logged-out visitors too). Tracking is fire-and-forget: it never
 * throws and never blocks the UI.
 *
 * "Unique" is decided here: the first time this browser fires a given
 * event (per market trip, for trip events) on a given UTC day.
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { WebEvent } from './events';

interface QueuedEvent { name: WebEvent; tripId?: string; unique: boolean }

const FLUSH_DELAY_MS = 3000;
const MAX_QUEUE = 20;
const SEEN_KEY = 'travelez_analytics_seen';

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let excluded = false;
let listenerAttached = false;

const enabled = (): boolean =>
    process.env.NODE_ENV === 'production' || process.env.REACT_APP_ANALYTICS_DEV === 'true';

const today = (): string => new Date().toISOString().slice(0, 10);

/** Returns true the first time `key` is seen today in this browser. */
function markSeenToday(key: string): boolean {
    try {
        const raw = localStorage.getItem(SEEN_KEY);
        let seen: { date: string; keys: string[] } = raw ? JSON.parse(raw) : { date: '', keys: [] };
        if (seen.date !== today()) seen = { date: today(), keys: [] };
        if (seen.keys.includes(key)) return false;
        seen.keys.push(key);
        localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
        return true;
    } catch {
        // Storage blocked (private mode etc.) — count, but can't dedupe.
        return true;
    }
}

async function flush(): Promise<void> {
    if (timer) { clearTimeout(timer); timer = null; }
    if (queue.length === 0) return;
    const events = queue;
    queue = [];
    try {
        await httpsCallable(functions, 'trackWebEvents')({ events });
    } catch {
        // Analytics is best-effort; drop on failure.
    }
}

function attachVisibilityListener(): void {
    if (listenerAttached || typeof document === 'undefined') return;
    listenerAttached = true;
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') void flush();
    });
}

export const analytics = {
    /** Record a usage event. Safe to call anywhere; never throws. */
    track(name: WebEvent, params?: { tripId?: string }): void {
        if (excluded || !enabled()) return;
        const tripId = params?.tripId;
        const unique = markSeenToday(tripId ? `${name}:${tripId}` : name);
        queue.push({ name, ...(tripId ? { tripId } : {}), unique });
        attachVisibilityListener();
        if (queue.length >= MAX_QUEUE) {
            void flush();
        } else if (!timer) {
            timer = setTimeout(() => void flush(), FLUSH_DELAY_MS);
        }
    },

    /** Exclude this browser session (e.g. admins) so internal use doesn't skew counts. */
    setExcluded(value: boolean): void {
        excluded = value;
        if (value) queue = [];
    },

    flush,
};
